import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { ButtonType } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import { ToastContext } from '@components/toasts/toast-context';
import { ToastType } from '@components/toasts/toast.types';
import { apiClient } from '@src/api/client';
import SampleTeaser from '@components/organisms/SampleTeaser';
import ShipmentInstructions from '@components/atoms/ShippmentInstructions';
import { ExternalLinks, Paths } from '@src/routes/paths';
import { Image } from '@components/atoms/Image';
import { useImpersonate } from '@src/contexts/ImpersonateContext';

export const ProfileView: React.FC = () => {
  const { t } = useTranslation('view', { keyPrefix: 'profile' });
  const { t: tToast } = useTranslation('toasts');
  const { t: tLegacy } = useTranslation('legacy');
  const state = useContext(ToastContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const sampleClaimed = searchParams.get('sampleClaimed');
  const sampleClaimedFailed = searchParams.get('claimingFailed');
  const { isImpersonating, impersonateSampleId } = useImpersonate();

  const { data, isLoading } = apiClient.useQuery('get', '/api/sample', {
    params: {
      query:
        isImpersonating && impersonateSampleId
          ? { impersonateSampleId }
          : undefined,
    },
  });
  const auth = useAuth();

  const latestActivatedSample = useMemo(() => {
    if (!data) {
      return null;
    }
    return data.find((sample) => sample.status === 'ACTIVATED');
  }, [data]);

  useEffect(() => {
    if (state && sampleClaimed === 'true') {
      state.addToast({
        title: tToast('sampleClaimed.title'),
        subTitle: tToast('sampleClaimed.subTitle'),
        toastType: ToastType.SUCCESS,
      });
      searchParams.delete('sampleClaimed');
      setSearchParams(searchParams, { replace: true });
    }
    if (state && sampleClaimedFailed === 'true') {
      state.addToast({
        title: tToast('sampleClaimedFailed.title'),
        subTitle: tToast('sampleClaimedFailed.subTitle'),
        toastType: ToastType.ERROR,
      });
      searchParams.delete('claimingFailed');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div>
      <div className="flex flex-row flex-wrap justify-between items-center mb-8">
        <h1 className="text-2xl font-bold my-2">
          {t('welcome', { name: auth.user?.profile.name })}
        </h1>
        {!isImpersonating && (
          <Link to={Paths.REGISTER_SAMPLE}>
            <Button buttonType={ButtonType.PRIMARY} label={t('addSample')} />
          </Link>
        )}
      </div>
      {latestActivatedSample && (
        <div className="bg-grey2 p-5 rounded-bonusLarge mb-8">
          <p className="mb-4 text-ivarioBlue font-bold">
            {t('latestSample.nextStep')}
          </p>
          <div className="flex flex-row">
            {latestActivatedSample.productImage && (
              <Image
                image={latestActivatedSample.productImage}
                alt={latestActivatedSample.productName}
                className="h-16 mr-6 hidden md:block object-contain w-max"
                sizes="64px"
              />
            )}
            <div className="w-full">
              <h2 className="mb-2.5 font-bold text-xl md:text-2xl">
                {t('latestSample.title', {
                  productName: latestActivatedSample.productName,
                })}
              </h2>
              <p className="font-light md:text-lg">
                {t('latestSample.infoText')}
              </p>

              <ShipmentInstructions sample={latestActivatedSample} />
            </div>
          </div>
        </div>
      )}

      <h1>{t('title')}</h1>

      {!isLoading && data && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-8">
          {data.map((sample) => (
            <SampleTeaser sample={sample} />
          ))}
        </div>
      )}
      <p className="mt-4 text-sm font-light">
        {tLegacy('sampleList')}
        <a
          href={ExternalLinks.LEGACY_LOGIN}
          className="underline text-ivarioBlue"
        >
          {tLegacy('sampleListLink')}
        </a>
        {tLegacy('sampleListOutro')}
      </p>
    </div>
  );
};
